import { useEffect, useState } from "react";
import { MdAdd, MdDelete, MdEdit, MdSettings } from "react-icons/md";
import Card from "components/card";
import { api } from "api";

const DEFAULT_SETTINGS = [
  { key: "app_name", value: "Mali Ajanda", description: "Uygulama adi" },
  { key: "app_version", value: "1.0.0", description: "Uygulama versiyonu" },
  { key: "support_email", value: "destek@maliajanda.com", description: "Destek email adresi" },
  { key: "support_phone", value: "", description: "Destek telefon numarasi" },
  { key: "website_url", value: "https://maliajanda.com", description: "Web sitesi adresi" },
  { key: "maintenance_mode", value: "false", description: "Bakim modu (true/false)" },
  { key: "min_app_version", value: "1.0.0", description: "Minimum uygulama versiyonu (eski surumler zorla guncellenir)" },
];

const Settings = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ key: "", value: "", description: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    api.getSettings().then(setItems).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditId(null);
    setForm({ key: "", value: "", description: "" });
    setError("");
    setShowForm(true);
  };

  const openEdit = (item: any) => {
    setEditId(item.id);
    setForm({ key: item.key, value: item.value, description: item.description || "" });
    setError("");
    setShowForm(true);
  };

  const handleSave = async () => {
    setError("");
    if (!form.key || !form.value) { setError("Key ve deger zorunludur"); return; }
    try {
      if (editId) {
        await api.updateSetting(editId, { value: form.value, description: form.description });
        setMsg("Ayar guncellendi!");
      } else {
        await api.createSetting(form);
        setMsg("Ayar eklendi!");
      }
      setShowForm(false);
      load();
      setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { setError(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Bu ayari silmek istediginize emin misiniz?")) return;
    await api.deleteSetting(id);
    load();
  };

  const seedDefaults = async () => {
    const existingKeys = items.map(i => i.key);
    let added = 0;
    for (const s of DEFAULT_SETTINGS) {
      if (!existingKeys.includes(s.key)) {
        await api.createSetting(s);
        added++;
      }
    }
    if (added > 0) {
      setMsg(`${added} varsayilan ayar eklendi!`);
      setTimeout(() => setMsg(""), 3000);
      load();
    } else {
      setMsg("Tum varsayilan ayarlar zaten mevcut");
      setTimeout(() => setMsg(""), 3000);
    }
  };

  return (
    <div>
      {msg && <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm text-green-700">{msg}</div>}

      <div className="mt-3 flex items-center justify-between">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">Uygulama Ayarlari ({items.length})</h4>
        <div className="flex gap-2">
          {items.length === 0 && (
            <button onClick={seedDefaults} className="flex items-center gap-2 rounded-xl bg-green-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-green-600 transition">
              <MdSettings className="h-5 w-5" /> Varsayilanlari Yukle
            </button>
          )}
          <button onClick={openCreate} className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition">
            <MdAdd className="h-5 w-5" /> Yeni Ayar
          </button>
        </div>
      </div>

      <Card extra="mt-4 p-4">
        {loading ? (
          <p className="p-4 text-center text-gray-500">Yukleniyor...</p>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <MdSettings className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 mb-2">Henuz ayar yok</p>
            <p className="text-sm text-gray-400">"Varsayilanlari Yukle" butonuyla temel ayarlari ekleyin</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700/50 transition">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-brand-500 bg-brand-50 px-2 py-0.5 rounded dark:bg-brand-900/20">{item.key}</span>
                    {item.description && <span className="text-xs text-gray-400">— {item.description}</span>}
                  </div>
                  <p className="mt-1 text-sm font-medium text-navy-700 dark:text-white">
                    {item.value === "true" ? (
                      <span className="text-green-600">Aktif</span>
                    ) : item.value === "false" ? (
                      <span className="text-red-500">Pasif</span>
                    ) : item.value}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => openEdit(item)} className="text-brand-500 hover:text-brand-700"><MdEdit className="h-5 w-5" /></button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-600"><MdDelete className="h-5 w-5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">{editId ? "Ayar Duzenle" : "Yeni Ayar"}</h3>
            {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Key *</label>
                <input type="text" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                  disabled={!!editId} placeholder="Orn: app_name"
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm font-mono outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white disabled:opacity-50" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Deger *</label>
                <input type="text" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder="Ayar degeri" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Aciklama</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Bu ayar ne ise yarar" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowForm(false)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleSave} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">{editId ? "Guncelle" : "Ekle"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
